import React from 'react';
import { LuCircleMinus } from 'react-icons/lu';

import { Account, CashFlowConfig } from '@ndvr/portfolio-api';

import { Card } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn } from '@/utils/cn';
import { formatDate } from 'utils';
import { incompleteInflationNote, totalPolicyWithdrawals, withdrawalTitle } from 'views/portfolio/policy-withdrawals';
import { frequencyTypeToLabel } from 'views/portfolio/PortfolioPage/utils/frequencyToLabel';

import { ValueTooltip, WholeDollars } from '../../reporting/HeroStatCard';
import {
    HEAD_CLASS,
    HEAD_ROW_CLASS,
    MV_CELL,
    NUMERIC_CELL,
    RIGHT_HEAD_CLASS,
    TABLE_CARD_HEADER_CLASS,
    TABLE_CLASS,
} from '../../reporting/table-styles';

interface PlannedWithdrawalsCardProps {
    // The scope's planned withdrawals (getScopedWithdrawals).
    withdrawals: CashFlowConfig[];
    // The household's accounts, to name the account a withdrawal is drawn from.
    accounts: Account[];
    isLoading: boolean;
    // The withdrawal plans failed to load; the list may be incomplete, so it is not shown.
    isError: boolean;
}

// Card title row, shared by the empty and filled states (AssetAllocation's Header).
const Header = () => (
    <div className={cn(TABLE_CARD_HEADER_CLASS, 'justify-between')}>
        <h3 className="font-heading text-cocoa-1000 text-xl">Planned withdrawals</h3>
        <span className="text-cocoa-400 text-xs">From Investment Policy → Withdrawal Plan</span>
    </div>
);

// The scope's Investment Policy withdrawals, listed as the Withdrawal Plan lists them: the rows
// show each withdrawal's base amount per occurrence, the footer the total projected over the next
// 12 months with inflation adjustments applied.
export const PlannedWithdrawalsCard = ({ withdrawals, accounts, isLoading, isError }: PlannedWithdrawalsCardProps) => {
    if (isLoading) {
        return <div className="bg-cocoa-100 h-64 w-full animate-pulse rounded-2xl" />;
    }

    if (isError || withdrawals.length === 0) {
        return (
            <Card className="border-border overflow-hidden rounded-2xl border bg-white">
                <Header />
                <p className="text-cocoa-400 px-4 pb-4 text-sm lg:px-6 lg:pb-6">
                    {isError ? 'Unable to load withdrawal plan.' : 'No withdrawals scheduled.'}
                </p>
            </Card>
        );
    }

    // NaN when a withdrawal's inflation settings are incomplete, as on the Withdrawal Plan.
    const total = totalPolicyWithdrawals(withdrawals, 1);
    const accountName = (accountGUID: string) =>
        accounts.find((account) => account.accountGUID === accountGUID)?.accountName.plainText ?? 'Unknown account';

    return (
        <Card className="border-border overflow-hidden rounded-2xl border bg-white">
            <Header />
            <Table className={cn(TABLE_CLASS, 'min-w-[720px]')}>
                <TableHeader>
                    <TableRow className={HEAD_ROW_CLASS}>
                        <TableHead className={HEAD_CLASS}>Purpose</TableHead>
                        <TableHead className={HEAD_CLASS}>Starts</TableHead>
                        <TableHead className={HEAD_CLASS}>Frequency</TableHead>
                        <TableHead className={HEAD_CLASS}>From account</TableHead>
                        <TableHead className={RIGHT_HEAD_CLASS}>Amount</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {withdrawals.map((config) => (
                        <TableRow key={config.cashFlowConfigGUID}>
                            <TableCell className="text-cocoa-1000 text-sm font-medium">
                                {/* The outlined icon tile of the account page's transaction rows. */}
                                <div className="flex items-center gap-3">
                                    <span className="flex size-7 shrink-0 items-center justify-center rounded-md border border-stone-200 text-stone-500">
                                        <LuCircleMinus aria-hidden="true" className="size-4" />
                                    </span>
                                    {withdrawalTitle(config)}
                                </div>
                            </TableCell>
                            <TableCell className="text-cocoa-700 text-sm whitespace-nowrap">
                                {formatDate(config.factorDateFrame.startDate, 'MMM d, yyyy')}
                            </TableCell>
                            <TableCell className="text-cocoa-900 text-sm font-semibold">
                                {frequencyTypeToLabel(config.factorDateFrame.frequencyType)}
                            </TableCell>
                            <TableCell className="text-cocoa-900 text-sm">
                                {config.accountGUID ? accountName(config.accountGUID) : 'Any Account'}
                            </TableCell>
                            <TableCell className={cn(NUMERIC_CELL, 'text-cocoa-900 font-medium')}>
                                <WholeDollars value={config.amount} />
                            </TableCell>
                        </TableRow>
                    ))}
                    <TableRow className="bg-cocoa-100/40 hover:bg-cocoa-100/40">
                        <TableCell colSpan={4} className="text-cocoa-1000 text-sm font-semibold">
                            Next 12 months
                        </TableCell>
                        <TableCell className={MV_CELL}>
                            {Number.isFinite(total) ? (
                                <WholeDollars value={total} />
                            ) : (
                                <ValueTooltip full={incompleteInflationNote(withdrawals)}>
                                    <span
                                        tabIndex={0}
                                        className="cursor-help underline decoration-dotted underline-offset-4"
                                    >
                                        Unavailable
                                    </span>
                                </ValueTooltip>
                            )}
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </Card>
    );
};
